import { Event, Person } from '@mui/icons-material';
import { Box, Divider } from '@mui/material';
import moment from 'moment/moment';
import { useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId } from '~/components/Auth';
import {
  EmployeeCalendar,
  useEmployeeCalendarPDFExport,
} from '~/components/EmployeeCalendar';
import {
  AnalyticsReportEmployeeScope,
  AnalyticsReportRoleScope,
  AnalyticsReportScope,
  AnalyticsReportTypes,
  useAbsencesReportData,
} from '~/data/reports';
import {
  ReportDataExportCustom,
  ReportDatasetLayout,
  ReportDatasetRowCount,
  ReportDatasetSelector,
  ReportDatasetSelectorParamsLayout,
  ReportDateRangeSelector,
  ReportDateRangeSelectorMode,
  ReportEmployeeScopeSelector,
  ReportEmployeeScopeValue,
  ReportViewLayout,
  useReportDataset,
  useReportNavigateToView,
} from '~/scenes/PortalModules/Analytics/common';
import { PersonnelGroupsFilters } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/PersonnelGroupsFilters';
import { ShowEmployeesModeSelector } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/ShowEmployeesModeSelector';
import { useAbsenceCalendarReport } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/useAbsenceCalendarReport';
import { useSxStyles } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/useStyles';

export const AbsenceCalendarReport = () => {
  const { t } = useTranslation();

  const sxStyles = useSxStyles();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const defaultReportSelection = useMemo(() => {
    const today = moment();

    const dateRange = {
      startDate: today.clone().subtract(1, 'month').toDate(),
      endDate: today.clone().add(2, 'months').toDate(),
    };

    return {
      reportType: {
        type: AnalyticsReportTypes.Team,
        role: AnalyticsReportRoleScope.AltManager,
        employeeId: loggedInEmployeeId,
        scope: AnalyticsReportScope.Direct,
      },
      params: {
        ...dateRange,
        empScope: AnalyticsReportEmployeeScope.CurrentEmployees,
      },
    };
  }, [loggedInEmployeeId]);

  const reportDatasetProps = useReportDataset({
    selection: defaultReportSelection,
    config: {
      options: {
        types: [
          AnalyticsReportTypes.Team,
          AnalyticsReportTypes.HR,
          AnalyticsReportTypes.Unit,
        ],
        roles: [
          AnalyticsReportRoleScope.Manager,
          AnalyticsReportRoleScope.DeputyManager,
        ],
        employeeScopes: [
          AnalyticsReportEmployeeScope.CurrentEmployees,
          AnalyticsReportEmployeeScope.AllEmployees,
          AnalyticsReportEmployeeScope.ActiveEmployees,
          AnalyticsReportEmployeeScope.EndedEmployees,
        ],
      },
    },
  });

  const { reportRange } = useMemo(() => {
    const reportRange = {
      startDate:
        reportDatasetProps.reportSelection?.params?.startDate ??
        defaultReportSelection.params.startDate,
      endDate:
        reportDatasetProps.reportSelection?.params?.endDate ??
        defaultReportSelection.params.endDate,
    };

    return {
      reportRange,
    };
  }, [reportDatasetProps, defaultReportSelection]);

  // Load data
  const {
    data,
    isLoading: isLoadingReportData,
    failedToLoad,
  } = useAbsencesReportData(reportDatasetProps.reportSelection);

  // Process data
  const {
    showEmployeesModeSelector,
    personnelGroupsFilters,
    counts,
    calendarProps,
    isLoading: isLoadingPublicHolidays,
  } = useAbsenceCalendarReport({
    data,
    ...reportRange,
  });

  const { pdfRef, onCalendarPDFExport } = useEmployeeCalendarPDFExport({
    title: t('laAbsCal'),
    rowsCount: calendarProps.listEmployees.length,
  });

  return (
    <ReportViewLayout
      heading={t('laAbsCal')}
      slots={{
        toolbar: {
          main: (
            <Box sx={sxStyles.reportToolbarWrapper}>
              <ReportDatasetLayout
                showHeading={true}
                sx={sxStyles.datasetLayout}
              >
                <ReportDatasetSelector
                  {...reportDatasetProps}
                  isLoading={isLoadingReportData || isLoadingPublicHolidays}
                  failedToLoad={failedToLoad}
                  renderCurrentParams={({ params }) => {
                    return (
                      <>
                        {`${moment(params?.startDate).format('L')} - ${moment(
                          params?.endDate,
                        ).format('L')}`}{' '}
                        <ReportEmployeeScopeValue empScope={params?.empScope} />
                      </>
                    );
                  }}
                  renderParamsFields={({ params, onChange, reportConfig }) => {
                    return (
                      <ReportDatasetSelectorParamsLayout>
                        <ReportDateRangeSelector
                          startDate={params.startDate}
                          endDate={params.endDate}
                          onRangeChange={(startDate, endDate) =>
                            onChange({
                              ...params,
                              startDate: startDate ?? params.startDate,
                              endDate: endDate ?? params.endDate,
                            })
                          }
                          mode={ReportDateRangeSelectorMode.DateRange}
                        />
                        <ReportEmployeeScopeSelector
                          reportConfig={reportConfig}
                          employeeScope={params?.empScope}
                          onChange={(empScope) => {
                            onChange({ ...params, empScope });
                          }}
                        />
                      </ReportDatasetSelectorParamsLayout>
                    );
                  }}
                />
                <ReportDatasetRowCount
                  count={counts.employees}
                  description={t('laTotalRecords')}
                  icon={<Person />}
                />
                <ReportDatasetRowCount
                  count={counts.absences}
                  description={t('laAbsences')}
                  icon={<Event />}
                />
              </ReportDatasetLayout>
              <Box sx={sxStyles.filtersLayout}>
                <PersonnelGroupsFilters {...personnelGroupsFilters} />
                <Divider
                  orientation={'vertical'}
                  sx={sxStyles.verticalDivider}
                />
                <ShowEmployeesModeSelector {...showEmployeesModeSelector} />
              </Box>
            </Box>
          ),
          actions: (
            <ReportDataExportCustom
              onCustomPDFExport={onCalendarPDFExport}
              isLoading={isLoadingReportData || isLoadingPublicHolidays}
            />
          ),
        },
      }}
    >
      <EmployeeCalendar
        ref={pdfRef}
        containerWidth={500}
        startDate={reportRange.startDate}
        endDate={reportRange.endDate}
        {...calendarProps}
        onEmployeeClick={goToEmployeeBasicDataView}
        isLoading={isLoadingReportData || isLoadingPublicHolidays}
      />
    </ReportViewLayout>
  );
};
